import { Link } from "react-router-dom";
import PixoSprite from "@/components/pixo/PixoSprite";

/** Shimmering placeholder block while data loads. */
export function Skel({ className = "", style }) {
  return <div className={`tier-skel rounded-sm ${className}`} style={style} aria-hidden="true" />;
}

/** Ranking-card shaped skeletons (category pages). */
export function RankingSkeleton({ rows = 5 }) {
  return (
    <div className="space-y-5" data-testid="ranking-skeleton" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="panel rounded-sm p-5 flex items-center gap-4 sm:gap-5" style={{ opacity: 1 - i * 0.14 }}>
          <Skel className="w-[52px] sm:w-[72px] h-10 sm:h-12" />
          <Skel className="w-12 h-12 sm:w-14 sm:h-14 !rounded-full shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <Skel className="h-5 w-3/5" />
            <Skel className="h-3 w-1/4" />
          </div>
          <Skel className="w-14 h-9" />
        </div>
      ))}
    </div>
  );
}

/** Profile-shaped skeleton. */
export function ProfileSkeleton() {
  return (
    <div className="pt-24 lg:pt-28 pb-20" aria-busy="true" data-testid="profile-skeleton">
      <div className="max-w-4xl mx-auto px-5 lg:px-10">
        <Skel className="h-3 w-40" />
        <div className="panel rounded-sm p-5 sm:p-8 mt-5 flex flex-col sm:flex-row gap-6">
          <div className="flex items-center gap-5">
            <Skel className="w-28 h-28 !rounded-full shrink-0" />
            <div className="space-y-2.5 flex-1">
              <Skel className="h-7 w-40" />
              <Skel className="h-3 w-32" />
              <Skel className="h-3 w-24" />
            </div>
          </div>
          <div className="flex-1 grid grid-cols-2 gap-1.5 content-end">
            {Array.from({ length: 6 }, (_, i) => <Skel key={i} className="h-10" />)}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Empty state with a sleepy Pixie: what's missing + what to do about it. */
export function PixieEmpty({ title, hint, cta, to, onClick, compact = false, testid }) {
  return (
    <div className={`flex ${compact ? "flex-row items-center gap-3 text-left py-3" : "flex-col items-center text-center py-12"} `} data-testid={testid}>
      <div className={compact ? "shrink-0 -my-2" : "mb-1"}>
        <PixoSprite gem="quartzo" equipped={{}} pose="sleep" scale={compact ? 1.5 : 2.5} shadow={false} />
      </div>
      <div className="min-w-0">
        <div className={`${compact ? "text-sm" : "font-display text-2xl uppercase tracking-tight"} text-stone-200 font-bold leading-tight`}>{title}</div>
        {hint && <div className={`text-stone-500 ${compact ? "text-xs" : "text-sm mt-1.5 max-w-sm"}`}>{hint}</div>}
        {cta && (to ? (
          <Link to={to} className="inline-block mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 px-3 py-2 rounded-sm hover:bg-amber-500/10">{cta}</Link>
        ) : (
          <button type="button" onClick={onClick} className="mt-3 text-[11px] font-bold uppercase tracking-[0.18em] text-amber-300 border border-amber-500/40 px-3 py-2 rounded-sm hover:bg-amber-500/10">{cta}</button>
        ))}
      </div>
    </div>
  );
}
